import {
  cloneElement,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  Box,
  FormControlLabel,
  Popover,
  PopoverProps,
  Typography,
} from '@mui/material';
import Checkbox from '../Checkbox';
import { ReactComponent as FilterIcon } from '../../assets/filter-2.svg';
import { ReactComponent as CloseIcon } from '../../assets/close-2.svg';
import { ReactComponent as PlusIcon } from '../../assets/plus-2.svg';
import { FilterItem, Operator } from '../../hooks/useUrlFilters';
import Button from '../Button';
import FilterSelector, {
  FilterOption,
  OnSubmitEventVal,
} from '../FilterSelector';
import { Flex, FlexSpace } from '../Flex';
import TextField from '../TextField';
import SvgIcon from '../SvgIcon';
import IconButton from '../IconButton';

const PopoverStyled = styled(Popover)`
  .MuiPaper-root {
    min-width: 800px;
    padding: 16px 24px;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.palette.common.gray8};
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
`;

const HeaderRow = styled(FlexSpace)`
  align-items: flex-start;
  gap: 16px;
`;

const TitleInput = styled(TextField)<{
  $isUntitled?: boolean;
  $isFocused?: boolean;
}>`
  width: 100%;

  .MuiInputBase-root {
    ${({ $isFocused }) =>
      !$isFocused &&
      `
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
  }

  .MuiInputBase-input {
    font-weight: 600;
    font-size: 18px;
    color: ${({ theme, $isUntitled }) =>
      $isUntitled ? theme.palette.common.gray4 : 'inherit'};
    ${({ $isFocused }) =>
      !$isFocused &&
      `
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
  }
`;

const FilterRow = styled(Flex)`
  align-items: center;
  gap: 12px;
`;

const LogicalOperator = styled(Typography)`
  min-width: 60px;
`;

const ActionsRow = styled(Flex)`
  gap: 8px;
`;

const TitleError = styled(Typography)<{ $visible: boolean }>`
  color: ${({ theme }) => theme.palette.error.main};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;

export interface MultiFilter {
  id: string;
  property?: string;
  operator?: Operator;
  value?: string[];
}

type CheckboxOption = {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

type Props = {
  filterOptions: FilterOption[];
  defaultOperators?: Operator[];
  defaultReadOnlyOperator?: boolean;
  multiselect?: boolean;
  onSave?: (title: string, filters: MultiFilter[]) => void;
  disabled?: boolean;
  popoverProps?: Partial<PopoverProps>;
  button?: ReactElement;
  initialTitle?: string;
  initialFilters?: FilterItem[];
  saveButtonText?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  checkboxOption?: CheckboxOption;
  sortOptions?: boolean;
};

const MultiFilterPopover = ({
  filterOptions,
  defaultOperators,
  defaultReadOnlyOperator,
  multiselect,
  onSave,
  disabled,
  popoverProps,
  button,
  initialTitle = '',
  initialFilters,
  saveButtonText = 'Save',
  isOpen,
  onOpenChange,
  checkboxOption,
  sortOptions,
}: Props) => {
  const buttonRef = useRef<HTMLElement | null>(null);
  const [title, setTitle] = useState(initialTitle);
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [showTitleError, setShowTitleError] = useState(false);

  const convertToMultiFilters = useCallback(
    (items?: FilterItem[]): MultiFilter[] => {
      if (items && items.length > 0) {
        return items.map((item) => ({
          id: crypto.randomUUID(),
          property: item.property,
          operator: item.operator,
          value: item.value,
        }));
      }
      return [{ id: crypto.randomUUID() }];
    },
    [],
  );

  const [filters, setFilters] = useState<MultiFilter[]>(() =>
    convertToMultiFilters(initialFilters),
  );

  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle);
      setFilters(convertToMultiFilters(initialFilters));
      setShowTitleError(false);
    }
  }, [isOpen, initialTitle, initialFilters, convertToMultiFilters]);

  const handleAddFilter = useCallback(() => {
    setFilters((prev) => [...prev, { id: crypto.randomUUID() }]);
  }, []);

  const handleRemoveFilter = useCallback((id: string) => {
    setFilters((prev) => {
      const newFilters = prev.filter((f) => f.id !== id);
      return newFilters.length === 0
        ? [{ id: crypto.randomUUID() }]
        : newFilters;
    });
  }, []);

  const handleFilterChange = useCallback(
    (id: string, vals: OnSubmitEventVal) => {
      setFilters((prev) =>
        prev.map((f) =>
          f.id === id
            ? {
                ...f,
                property: vals.property,
                operator: vals.operator,
                value: vals.value,
              }
            : f,
        ),
      );
    },
    [],
  );

  const handleClearAll = useCallback(() => {
    setFilters([{ id: crypto.randomUUID() }]);
  }, []);

  const hasTitle = !!title.trim();

  const hasCompleteFilter = useMemo(
    () =>
      filters.some(
        (f) => f.property && f.operator && f.value && f.value.length > 0,
      ),
    [filters],
  );

  const isSaveDisabled = useMemo(() => !hasCompleteFilter, [hasCompleteFilter]);

  const isClearDisabled = useMemo(
    () => !hasCompleteFilter,
    [hasCompleteFilter],
  );

  const handleSave = useCallback(() => {
    if (!hasTitle) {
      setShowTitleError(true);
      return;
    }
    onSave?.(
      title,
      filters.filter((f) => f.property && f.operator && f.value),
    );
    onOpenChange(false);
  }, [hasTitle, title, filters, onSave, onOpenChange]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Box>
      {button ? (
        cloneElement(button, {
          ref: buttonRef,
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            onOpenChange(true);
            button.props.onClick?.(e);
          },
        })
      ) : (
        <Button
          ref={buttonRef as any}
          variant="outlined"
          onClick={() => {
            onOpenChange(true);
          }}
          startIcon={<FilterIcon />}
          disabled={disabled}
        >
          Filter
        </Button>
      )}
      <PopoverStyled
        anchorEl={buttonRef.current}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 50,
          horizontal: 'left',
        }}
        {...popoverProps}
      >
        <HeaderRow>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TitleInput
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (showTitleError && e.target.value.trim()) {
                  setShowTitleError(false);
                }
              }}
              onFocus={() => setIsTitleFocused(true)}
              onBlur={() => setIsTitleFocused(false)}
              placeholder="Untitled"
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              inputProps={{
                spellCheck: false,
              }}
              $isUntitled={!title}
              $isFocused={isTitleFocused}
              autoFocus
            />
            <TitleError $visible={showTitleError}>
              Please enter a title to proceed
            </TitleError>
          </Box>
          <ActionsRow>
            {checkboxOption && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxOption.checked}
                    onChange={(e) => checkboxOption.onChange(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {checkboxOption.label}
                  </Typography>
                }
              />
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              {saveButtonText}
            </Button>
          </ActionsRow>
        </HeaderRow>

        {filters.map((filter, index) => (
          <FilterRow key={filter.id}>
            <LogicalOperator variant="body2">
              {index === 0 ? 'When' : 'And'}
            </LogicalOperator>
            <Box sx={{ flex: 1 }}>
              <FilterSelector
                key={filter.id}
                filterOptions={filterOptions}
                defaultOperators={defaultOperators}
                defaultReadOnlyOperator={defaultReadOnlyOperator}
                multiselect={multiselect}
                disabled={disabled}
                onSubmit={(vals) => handleFilterChange(filter.id, vals)}
                resetOnClose={false}
                initialProperty={filter.property}
                initialOperator={filter.operator}
                initialValue={filter.value}
                autoApplyFreeText
                sortOptions={sortOptions}
              />
            </Box>
            <IconButton onClick={() => handleRemoveFilter(filter.id)}>
              <SvgIcon width={14} fillColor="black">
                <CloseIcon />
              </SvgIcon>
            </IconButton>
          </FilterRow>
        ))}

        <Flex sx={{ alignItems: 'center' }}>
          <Button
            color="secondary"
            startIcon={<PlusIcon />}
            onClick={handleAddFilter}
          >
            Add filter
          </Button>
          <Button
            color="secondary"
            onClick={handleClearAll}
            disabled={isClearDisabled}
          >
            Clear all
          </Button>
        </Flex>
      </PopoverStyled>
    </Box>
  );
};

export default MultiFilterPopover;
